import type { MutableRefObject } from 'react';

import { useEffect, useRef } from 'react';

export const useIntersectionObserver = (
  callback: (
    entry: IntersectionObserverEntry,
    observer: IntersectionObserver | null,
  ) => void,
  ref: MutableRefObject<Element>,
  options: IntersectionObserverInit,
): void => {
  const callbackRef = useRef(callback);
  const memoizedCallback = callbackRef.current;

  if (typeof window === 'undefined') {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    // if browser does not support intersection observer set item as intersecting
    return memoizedCallback(
      { isIntersecting: true } as IntersectionObserverEntry,
      null,
    );
  }

  const observer = useRef<IntersectionObserver>(null);

  useEffect(() => {
    const item = ref?.current;

    const unobserve = () => {
      observer?.current?.disconnect();
    };

    if (observer?.current) {
      unobserve();
    }

    if (item) {
      observer.current = new IntersectionObserver(
        ([entry], currentObserver) => memoizedCallback(entry, currentObserver),
        options,
      );

      observer.current.observe(item);
    }

    return () => unobserve();
  }, [ref]);
};
